import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Box, Chip, FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import {
  BUSINESS_MODELS,
  STORE_MODULES,
  type BusinessModelCode,
} from '@iobiz/shared'
import { api } from '../../api/client.js'
import { PhotoGallery } from '../../ui/PhotoGallery.js'

/**
 * Le formulaire d'une boutique — création ET modification.
 *
 * ⭐ UN SEUL composant pour les deux écrans. Deux formulaires jumeaux divergent
 * toujours : un champ ajouté d'un côté manque de l'autre, et personne ne s'en
 * aperçoit avant qu'un utilisateur ne cherche en vain à modifier ce qu'il vient
 * de saisir. L'égalité devient ici structurelle au lieu d'être surveillée.
 *
 * Ce qui diffère entre les deux modes est explicite et se compte sur les
 * doigts : l'adresse web se saisit à la création et s'affiche ensuite (elle ne
 * se renomme pas), le statut n'existe qu'en modification, l'administrateur
 * qu'à la création — ensuite les accès se règlent dans leur propre écran.
 */

export interface DonneesBoutique {
  nom: string
  slug: string
  pays: string
  modele: BusinessModelCode
  adresse: string
  telephone: string
  courriel: string
  siteWeb: string
  raisonSociale: string
  numeroFiscal: string
  statut: string
  modules: Record<string, boolean>
  logo: string | null
  adminEmail: string
  adminPrenom: string
  adminNom: string
}

export function donneesVides(): DonneesBoutique {
  return {
    nom: '',
    slug: '',
    pays: 'CM',
    modele: 'retail',
    adresse: '',
    telephone: '',
    courriel: '',
    siteWeb: '',
    raisonSociale: '',
    numeroFiscal: '',
    statut: 'active',
    modules: {},
    logo: null,
    adminEmail: '',
    adminPrenom: '',
    adminNom: '',
  }
}

/**
 * ⚠️ Masque de saisie : transforme en identifiant d'URL au fil de la frappe.
 * Il ne se justifie que parce que le champ s'intitule « Adresse web » — sous
 * l'intitulé « Adresse », il a conduit à y saisir une rue, aussitôt réduite en
 * charabia.
 */
export function slugifier(nom: string): string {
  return nettoyer(nom).replace(/[.-]+$/g, '')
}

/**
 * Masque de SAISIE — volontairement plus permissif que `slugifier`.
 *
 * ⚠️ Il conserve un séparateur final, là où la version stricte le retire. Sans
 * cette nuance, taper « ma. » est IMPOSSIBLE : à l'instant de la frappe le
 * point est en dernière position, donc supprimé — le caractère semble ne pas
 * s'inscrire. Coller « ma.boutique » fonctionnait, puisque le point y est au
 * milieu, ce qui rendait le défaut déroutant.
 *
 * La forme stricte s'applique à la sortie du champ et avant envoi : on écrit
 * librement, on normalise une fois la saisie finie.
 */
export function slugifierSaisie(nom: string): string {
  return nettoyer(nom).replace(/[.-]{2,}$/g, (m) => (m.includes('.') ? '.' : '-'))
}

/** Partie commune : minuscules sans accents, séparateurs isolés, pas de tête. */
function nettoyer(nom: string): string {
  return nom
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // Le point est conservé : il est admis dans une adresse web.
    .replace(/[^a-z0-9.-]+/g, '-')
    // Jamais deux séparateurs de suite, au milieu de la chaîne.
    .replace(/([.-])[.-]+(?=[a-z0-9])/g, '$1')
    .replace(/^[.-]+/g, '')
    .slice(0, 40)
}

function Bloc({ titre, aide, children }: { titre: string; aide?: string; children: ReactNode }) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '.08em', fontWeight: 600 }}
      >
        {titre}
      </Typography>
      {aide && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
          {aide}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          mt: aide ? 0 : 2,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export function FormulaireBoutique({
  valeurs,
  onChange,
  mode,
  repere,
}: {
  valeurs: DonneesBoutique
  onChange: (maj: Partial<DonneesBoutique>) => void
  mode: 'creation' | 'modification'
  /** Informations non modifiables affichées en modification. */
  repere?: { schemaName: string; devise: string; membres: number; creeLe: string }
}): JSX.Element {
  const creation = mode === 'creation'
  const [listePays, setListePays] = useState<
    { code: string; name: string; currency: string }[]
  >([])
  const [slugTouche, setSlugTouche] = useState(!creation)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (listePays.length > 0) return
    void api
      .get<{ data: { code: string; name: string; currency: string }[] }>('/countries')
      .then((r) => setListePays(r.data))
      .catch(() => undefined)
  }, [listePays.length])

  // À la création, l'adresse web suit le nom tant qu'on n'y a pas touché.
  //  `onChange` est volontairement hors des dépendances : la fonction est
  //  recréée à chaque rendu du parent, et l'inclure relancerait l'effet en
  //  boucle — le slug se réécrirait indéfiniment.
  const nomCourant = valeurs.nom
  useEffect(() => {
    if (creation && !slugTouche) onChangeRef.current({ slug: slugifier(nomCourant) })
  }, [nomCourant, slugTouche, creation])

  const devise = listePays.find((p) => p.code === valeurs.pays)?.currency

  return (
    <Box sx={{ display: 'grid', gap: 5 }}>
      {repere && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip size="small" label={`/s/${valeurs.slug}`} />
          <Chip size="small" variant="outlined" label={repere.devise} />
          <Chip
            size="small"
            variant="outlined"
            label={`${repere.membres} utilisateur${repere.membres > 1 ? 's' : ''}`}
          />
          <Chip size="small" variant="outlined" label={repere.schemaName} />
          <Chip size="small" variant="outlined" label={`créée le ${repere.creeLe}`} />
        </Box>
      )}

      <Bloc titre="Identité">
        {/* ⭐ Le logo OUVRE la fiche : c'est lui qui identifie la boutique dans
            le sélecteur et dans l'entête du menu, avant même son nom.
            MÊME composant que les photos d'article — même tuile, même
            glisser-déposer, même redimensionnement — borné à un seul visuel. */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <PhotoGallery
            values={valeurs.logo ? [valeurs.logo] : []}
            onChange={(images) => onChange({ logo: images[0] ?? null })}
            max={1}
            legende="Logo de la boutique"
          />
        </Box>
        <TextField
          label="Nom de la boutique"
          value={valeurs.nom}
          required
          autoFocus={creation}
          onChange={(e) => onChange({ nom: e.target.value })}
          sx={{ gridColumn: '1 / -1' }}
        />
        <TextField
          select
          label="Pays"
          value={valeurs.pays}
          onChange={(e) => onChange({ pays: e.target.value })}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          helperText={
            creation
              ? devise
                ? `Devise ${devise} — fixe aussi langue et fuseau`
                : 'Fixe la devise, la langue et le fuseau'
              : // ⚠️ En modification, changer de pays ne change PAS la devise :
                // des montants déjà enregistrés la portent, et les convertir
                // rétroactivement fausserait l'historique comptable.
                'La devise reste celle d’origine — les montants enregistrés la portent'
          }
        >
          {listePays.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </TextField>
        <TextField
          select
          label="Modèle de commerce"
          value={valeurs.modele}
          onChange={(e) => onChange({ modele: e.target.value as BusinessModelCode })}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          helperText={BUSINESS_MODELS.find((m) => m.code === valeurs.modele)?.description}
        >
          {BUSINESS_MODELS.map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </TextField>
      </Bloc>

      <Bloc
        titre="Adresse web"
        aide={
          creation
            ? 'Le lien par lequel on accède à la boutique.'
            : '⚠️ La modifier casse les liens enregistrés : favoris, raccourcis, adresses partagées. L’ancienne adresse ne redirige pas.'
        }
      >
        <TextField
          label="Adresse web"
          value={valeurs.slug}
          required
          onChange={(e) => {
            setSlugTouche(true)
            onChange({ slug: slugifierSaisie(e.target.value) })
          }}
          onBlur={() => onChange({ slug: slugifier(valeurs.slug) })}
          helperText={valeurs.slug ? `iobiz.fr/s/${valeurs.slug}` : 'Engendrée depuis le nom'}
          sx={{ gridColumn: '1 / -1' }}
        />
      </Bloc>

      <Bloc titre="Coordonnées" aide="Elles figureront sur les documents émis par la boutique.">
        <TextField
          label="Adresse"
          value={valeurs.adresse}
          placeholder="Rue 140, Porte 99, Bamako"
          onChange={(e) => onChange({ adresse: e.target.value })}
          sx={{ gridColumn: '1 / -1' }}
        />
        <TextField
          label="Téléphone"
          value={valeurs.telephone}
          onChange={(e) => onChange({ telephone: e.target.value })}
        />
        <TextField
          label="Courriel"
          type="email"
          value={valeurs.courriel}
          placeholder="contact@boutique.cm"
          onChange={(e) => onChange({ courriel: e.target.value })}
        />
        <TextField
          label="Site web"
          value={valeurs.siteWeb}
          placeholder="boutique.cm"
          onChange={(e) => onChange({ siteWeb: e.target.value })}
          sx={{ gridColumn: '1 / -1' }}
        />
      </Bloc>

      <Bloc titre="Informations légales">
        <TextField
          label="Raison sociale"
          value={valeurs.raisonSociale}
          placeholder="À défaut, le nom de la boutique"
          onChange={(e) => onChange({ raisonSociale: e.target.value })}
        />
        <TextField
          label="N° fiscal"
          value={valeurs.numeroFiscal}
          onChange={(e) => onChange({ numeroFiscal: e.target.value })}
        />
      </Bloc>

      <Box>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', letterSpacing: '.08em', fontWeight: 600 }}
        >
          Modules disponibles
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
          Toutes les entrées du menu se coupent, sans exception. Un module désactivé reste VISIBLE
          mais grisé et inaccessible — masquer une entrée fait croire à une panne, la griser dit
          qu’elle existe et qu’elle peut revenir. Le réglage se fait d’ici, hors de la boutique :
          même « Paramètres » coupé, vous rouvrez l’accès depuis cet écran.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            px: 3,
            py: 2,
          }}
        >
          {STORE_MODULES.map((m) => (
            <FormControlLabel
              key={m.code}
              sx={{ m: 0, py: 0.5 }}
              control={
                <Switch
                  size="small"
                  checked={valeurs.modules[m.code] !== false}
                  onChange={(e) =>
                    onChange({ modules: { ...valeurs.modules, [m.code]: e.target.checked } })
                  }
                />
              }
              label={<Typography variant="body2">{m.label}</Typography>}
            />
          ))}
        </Box>
      </Box>

      {creation ? (
        <Bloc
          titre="Administrateur"
          aide="Laissez vide pour en être vous-même l’administrateur. Sinon, cette personne recevra une invitation pour choisir son mot de passe."
        >
          <TextField
            label="Adresse e-mail"
            type="email"
            value={valeurs.adminEmail}
            onChange={(e) => onChange({ adminEmail: e.target.value })}
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label="Prénom"
            value={valeurs.adminPrenom}
            onChange={(e) => onChange({ adminPrenom: e.target.value })}
          />
          <TextField
            label="Nom"
            value={valeurs.adminNom}
            onChange={(e) => onChange({ adminNom: e.target.value })}
          />
        </Bloc>
      ) : (
        <Bloc titre="Statut">
          <TextField
            select
            label="Statut"
            value={valeurs.statut}
            onChange={(e) => onChange({ statut: e.target.value })}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            helperText={
              valeurs.statut === 'suspended'
                ? 'Suspendue : ses utilisateurs ne peuvent plus y entrer. Les données restent intactes.'
                : valeurs.statut === 'archived'
                  ? 'Archivée : elle disparaît des listes. Réversible, mais à réserver aux boutiques fermées.'
                  : 'Active : fonctionnement normal.'
            }
            sx={{ gridColumn: '1 / -1' }}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspendue</option>
            <option value="archived">Archivée</option>
          </TextField>
        </Bloc>
      )}
    </Box>
  )
}
